import { Button } from "@/components/ui/button";
import {
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Priority, User } from "@/types";
import { ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import type { FieldArrayWithId } from "react-hook-form";
import { useFormContext, useWatch } from "react-hook-form";
import { AssigneeRow } from "./AssigneeRow";
import { AssigneeLoadBadge } from "./AssigneeLoadBadge";
import {
    AssigneeLoad,
    avatarAssigneeClass,
    avatarAssigneeFallbackClass,
    cardBodyClass,
    cardClass,
    cardHeaderClass,
    DisplayUser,
    isAssigneeAddDisabledAtCreate,
    sectionKickerClass,
} from "./priority-form-constants";
import type { PriorityFormValues } from "./priority-form-schema";

export interface AssignTeamCardProps {
    isEdit: boolean;
    assignmentFields: FieldArrayWithId<PriorityFormValues, "assignments", "id">[];
    users: User[];
    isLoadingUsers: boolean;
    userSearch: string;
    setUserSearch: (s: string) => void;
    onAssigneePidsChange: (pids: string[]) => void;
    onSetDri: (driPid: string) => void;
    initialData?: Partial<Priority>;
    assigneeNameByPid: Map<string, string>;
    userByPid: Map<string, DisplayUser>;
    loadByPid?: Map<string, AssigneeLoad>;
    /**
     * True when the current user may not change task-level fields. The roster (add/remove/DRI) locks,
     * and so does every row except the current user's own — an assignee always owns their own row.
     */
    readOnly?: boolean;
}

export function AssignTeamCard({
    isEdit,
    assignmentFields,
    users,
    isLoadingUsers,
    userSearch,
    setUserSearch,
    onAssigneePidsChange,
    onSetDri,
    initialData,
    assigneeNameByPid,
    userByPid,
    loadByPid,
    readOnly = false,
}: AssignTeamCardProps) {
    const { user } = useAuth();
    const form = useFormContext<PriorityFormValues>();
    const [openAssigneePicker, setOpenAssigneePicker] = useState(false);

    const watchedAssignments = useWatch({ control: form.control, name: "assignments" });

    const addableUsers = users.filter(
        (u) => !watchedAssignments.some((a) => a.userPid === u.pid)
    );

    const initialAssigneeBlockerByPid = useMemo(() => {
        const m = new Map<string, { pid: string; fullName: string }>();
        initialData?.assignments?.forEach((a) => {
            if (a.blocker) m.set(a.assignedUser.pid, { pid: a.blocker.pid, fullName: a.blocker.fullName });
        });
        return m;
    }, [initialData]);

    const removeAssignee = (pid: string) => {
        onAssigneePidsChange(watchedAssignments.filter((a) => a.userPid !== pid).map((a) => a.userPid));
    };

    return (
        <div className={cardClass}>
            <div className={cn(cardHeaderClass, "min-w-0 gap-3")}>
                <span className="min-w-0 shrink text-sm font-medium">Assign team</span>
                <span className="shrink-0 whitespace-nowrap text-right text-xs text-muted-foreground">
                    DRI is responsible for unblocking the team
                </span>
            </div>
            <div className={cardBodyClass}>
                <FormField
                    control={form.control}
                    name="assignments"
                    render={() => (
                        <FormItem className="mb-2">
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <div className="mb-3 flex flex-col gap-2">
                    {assignmentFields.map((row, index) => {
                        const isDri = watchedAssignments[index]?.isDri ?? false;
                        const isCurrentUser = row.userPid === user?.pid;
                        return (
                            // Per-row fieldset, not one around the whole card: a non-owner's own row must
                            // stay editable (PATCH /assignments/me) while everyone else's rows lock whole.
                            // rowLocked is passed on top of the fieldset because the row's Radix widgets
                            // open on pointerdown, which a disabled fieldset does not suppress.
                            <fieldset
                                key={row.id}
                                disabled={readOnly && !isCurrentUser}
                                className="m-0 min-w-0 border-0 p-0"
                            >
                                <AssigneeRow
                                    row={row}
                                    index={index}
                                    isEdit={isEdit}
                                    isDri={isDri}
                                    rosterLocked={readOnly}
                                    rowLocked={readOnly && !isCurrentUser}
                                    isCurrentUser={isCurrentUser}
                                    assigneeNameByPid={assigneeNameByPid}
                                    userByPid={userByPid}
                                    load={loadByPid?.get(row.userPid)}
                                    initialAssigneeBlocker={initialAssigneeBlockerByPid.get(row.userPid)}
                                    onRemove={removeAssignee}
                                    onSetDri={onSetDri}
                                />
                            </fieldset>
                        );
                    })}
                </div>

                <Popover open={openAssigneePicker} onOpenChange={setOpenAssigneePicker}>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            aria-expanded={openAssigneePicker}
                            className={cn(
                                "h-9 w-full justify-between text-left text-sm font-normal",
                                addableUsers.length === 0 && "text-muted-foreground"
                            )}
                            disabled={isLoadingUsers || readOnly}
                            title={readOnly ? "Only the DRI, the creator or an admin can change assignees." : undefined}
                        >
                            <span className="truncate">
                                {addableUsers.length === 0 ? "No more members to add" : "Add team member"}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command shouldFilter={false}>
                            <CommandInput
                                placeholder="Search user..."
                                onValueChange={setUserSearch}
                                value={userSearch}
                            />
                            <CommandList>
                                <CommandEmpty>
                                    {isLoadingUsers
                                        ? "Loading..."
                                        : addableUsers.length === 0
                                            ? "No more members to add."
                                            : "No user found."}
                                </CommandEmpty>
                                <CommandGroup>
                                    {addableUsers.map((user) => {
                                        const userLoad = loadByPid?.get(user.pid);
                                        const addBlocked = !isEdit && isAssigneeAddDisabledAtCreate(userLoad);
                                        return (
                                            <CommandItem
                                                key={user.pid}
                                                value={user.fullName}
                                                disabled={addBlocked}
                                                title={
                                                    addBlocked
                                                        ? `${user.fullName} already has 2 tasks in Queue. Free up a slot before adding them.`
                                                        : undefined
                                                }
                                                onSelect={() => {
                                                    if (addBlocked) return;
                                                    const current = watchedAssignments.map((a) => a.userPid);
                                                    if (!current.includes(user.pid)) {
                                                        onAssigneePidsChange([...current, user.pid]);
                                                    }
                                                    setOpenAssigneePicker(false);
                                                }}
                                            >
                                                <UserAvatar
                                                    src={user.avatarUrl}
                                                    name={user.fullName}
                                                    className={cn(avatarAssigneeClass, "mr-2")}
                                                    fallbackClassName={avatarAssigneeFallbackClass}
                                                />
                                                <span className="min-w-0 flex-1 truncate">{user.fullName}</span>
                                                <AssigneeLoadBadge load={userLoad} className="ml-2 shrink-0" />
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>

                <p className="mt-3 rounded-md border-l-2 border-primary/25 bg-muted/40 px-2.5 py-2 text-xs leading-relaxed text-muted-foreground">
                    <strong className="font-medium text-foreground">IC Task</strong> helps the team
                    see who owns which slice — shown on the team board and manager view.
                </p>
            </div>
        </div>
    );
}
